import { useForm } from 'react-hook-form';
import { FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import { Button, Container, Paper, Stack, Typography, Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { dateFormatter, dropdownFetch, request, toast } from '../../../../../utils';
import { useLocation } from 'react-router-dom';
import { useDoctorScope } from '../../../../../customHooks/useDoctorScope';

const DOCTOR_BILLING_TABLE = [
    { header: 'Type', accessor: 'type' },
    { header: 'Bill No.', accessor: 'bill_no' },
    { header: 'Date', accessor: (row) => (row.date ? dateFormatter(row.date) : '-') },
    { header: 'Patient Name', accessor: 'patient_name' },
    { header: 'UHID / IPD No.', accessor: (row) => (row.type === 'IPD' ? row.ipd_no : row.uhid) || '-' },
    { header: 'Sponsor', accessor: 'sponsor_name' },
    { header: 'Net Amount', accessor: 'net_amt' }
];

const initialBillingDataObj = {
    data: [],
    loading: false
};

export const ViewDoctorVisitsAndBills = () => {
    const { pathname } = useLocation();
    const lastPathSegment = pathname.split('/').pop();
    const [doctorDropdown, setDoctorDropdown] = useState([]);
    const [billingDataObj, setBillingDataObj] = useState(initialBillingDataObj);

    const doctorScope = useDoctorScope();

    const methods = useForm({
        defaultValues: {
            doctor: '',
            from_date: '',
            to_date: ''
        },
        mode: 'all'
    });
    const { watch, setValue } = methods;

    useEffect(() => {
        if (doctorScope.isDoctor && doctorScope.doctorId) {
            setValue('doctor', doctorScope.doctorId);
        }
    }, [doctorScope.isDoctor, doctorScope.doctorId, setValue]);

    const getDoctorDropdown = useCallback(async () => {
        const data = await dropdownFetch('doctor');
        setDoctorDropdown(data);
    }, []);

    useEffect(() => {
        getDoctorDropdown();
    }, [getDoctorDropdown]);

    const handleSearch = async () => {
        const doctorId = watch('doctor');
        if (!doctorId) {
            toast('Please select a doctor first', { variant: 'warning' });
            return;
        }
        setBillingDataObj({ loading: true, data: [] });
        // Doctor logins are hard-scoped server-side to their own id regardless of
        // what's sent here (see doctorScopeGuard on GET /doctor-billing/list).
        const response = await request('/doctor-billing-list', {
            method: 'GET',
            query: { doctorId, from_date: watch('from_date'), to_date: watch('to_date') }
        });
        if (response?.success) {
            setBillingDataObj({ loading: false, data: response.data || [] });
        } else {
            toast('Failed to load doctor billing', { variant: 'error' });
            setBillingDataObj({ loading: false, data: [] });
        }
    };

    return (
        <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
            <Container
                sx={{
                    mt: 2, p: 0.2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1, width: '100%',
                    maxWidth: 'none', minWidth: '100%', boxSizing: 'border-box'
                }}
            >
                <FormProvider methods={methods}>
                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ pb: 0.5, mb: 3, borderBottom: '1px solid', borderColor: 'grey.300' }}
                    >
                        <Typography color="primary" variant="h3" sx={{ fontWeight: 700 }}>
                            View Doctor Visits and Bills
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 }}>
                            <Typography sx={{ flex: 0.25, fontWeight: 700, fontSize: '1.4rem' }}>Doctor</Typography>
                            <SelectTag
                                formControlSX={{ flex: 0.75 }}
                                name="doctor"
                                placeholder="Select Doctor"
                                disabled={doctorScope.isDoctor}
                                menus={doctorScope.isDoctor ? doctorDropdown.filter((d) => d._id === doctorScope.doctorId) : doctorDropdown}
                            />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 }}>
                            <Typography sx={{ flex: 0.25, fontWeight: 700, fontSize: '1.4rem' }}>From Date</Typography>
                            <TextField sx={{ flex: 0.35 }} name="from_date" type="date" />
                            <Typography sx={{ flex: 0.25, fontWeight: 700, fontSize: '1.4rem' }}>To Date</Typography>
                            <TextField sx={{ flex: 0.35 }} name="to_date" type="date" />
                        </Stack>
                        <Button
                            id={`${lastPathSegment}Read`}
                            variant="contained"
                            disabled={!watch('doctor')}
                            onClick={handleSearch}
                        >
                            Search
                        </Button>
                    </Stack>

                    <Box sx={{ minHeight: '45rem', border: '1px solid rgba(0,0,0,0.27)' }}>
                        <Table
                            data={billingDataObj?.data || []}
                            loading={billingDataObj.loading}
                            columns={DOCTOR_BILLING_TABLE}
                            size="small"
                        />
                    </Box>
                </FormProvider>
            </Container>
        </Paper>
    );
};
